import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
// Components
import { Card, Table, SMBSelect } from '../../../../components';
import { Close, DeleteForever, Add, Hail } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Grid,
  MenuItem,
  Typography,
  Divider,
  TextField,
  ButtonGroup,
  Alert,
  AlertTitle,
  Box
} from '@mui/material';
import { IAvailableNeighborhoodsForemanUser, IDropdownResponse } from '../../../../models';
import {
  getForemanUsersLookup,
  getForemanUserNeighborhoodNames,
  getNeighborhoodForemanUsers,
  addForemanToNeighborhood,
  deleteForemanUserNeighborhood
} from '../../../../fetch';

export const Foremen = ({ neighborhoodId }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [selectedForemanUserId, setSelectedForemanUserId] = useState<string | number>('');
  const [selectedForemanUserName, setSelectedForemanUserName] = useState<string>('');
  const [foremanUserPriorityOrder, setForemanUserPriorityOrder] = useState<string | number>('');
  const [isDeleting, setDeleting] = useState<{ [id: number]: boolean }>({});

  const classes = useStyles();

  const formatDetails = (details: string[]) => {
    let neighborhoods = details.join(', ');

    return (
      <>
        {neighborhoods.length !== 0 ? (
          <Alert severity="info">
            <AlertTitle className={classes.alertTitle}>
              Foreman {selectedForemanUserName} is currently assigned to:
            </AlertTitle>
            {neighborhoods}
          </Alert>
        ) : (
          <Alert severity="info">
            <AlertTitle className={classes.alertTitle}>
              Foreman {selectedForemanUserName} is not assigned to any neighborhoods.
            </AlertTitle>
          </Alert>
        )}
      </>
    );
  };

  const {
    isLoading: isLoadingAvailableForemanUsers,
    data: availableForemanUsers,
    refetch: refetchAvailableForemanUsers
  } = useQuery<IDropdownResponse[], Error>(
    ['getForemanUsersLookup'],
    () => getForemanUsersLookup(),
    {
      notifyOnChangeProps: 'tracked'
    }
  );

  const { isLoading: isLoadingForemanUserNeighborhoodNames, data: foremanUserNeighborhoodNames } =
    useQuery<string[], Error>(
      ['getForemanUserNeighborhoodNames', selectedForemanUserId],
      () => getForemanUserNeighborhoodNames(selectedForemanUserId),
      {
        notifyOnChangeProps: 'tracked',
        enabled: !!selectedForemanUserId
      }
    );

  const {
    isLoading: isLoadingNeighborhoodForemanUsers,
    data: neighborhoodsForemanUsers,
    refetch: refetchNeighborhoodForemanUsers
  } = useQuery<IAvailableNeighborhoodsForemanUser[], Error>(
    ['getNeighborhoodForemanUsers', neighborhoodId],
    () => getNeighborhoodForemanUsers(neighborhoodId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!neighborhoodId
    }
  );

  const handleDelete = async (foremanUserId: number | string) => {
    const result = window.confirm('Are you sure you want to delete this Foreman?');
    if (result) {
      setDeleting({ [neighborhoodId]: true });
      try {
        await deleteForemanUserNeighborhood(foremanUserId, neighborhoodId);
        refetchNeighborhoodForemanUsers();
        enqueueSnackbar('Foreman removed!', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? 'Error deleting foreman.', {
          variant: 'error'
        });
      } finally {
        setDeleting({ [neighborhoodId]: false });
      }
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Priority',
        accessor: 'priorityOrder',
        sort: false
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IAvailableNeighborhoodsForemanUser } };
        }) => {
          return (
            <>
              <LoadingButton
                loading={isDeleting[original.foremanUserId]}
                loadingPosition="start"
                color="error"
                disabled={isDeleting[original.foremanUserId]}
                startIcon={<DeleteForever />}
                onClick={() => {
                  handleDelete(original.foremanUserId);
                }}
              >
                Delete
              </LoadingButton>
            </>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card title="Foremen" icon={<Hail />}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <SMBSelect
            variant="outlined"
            disabled={isLoadingAvailableForemanUsers}
            name={`foreman`}
            label={`Foreman`}
            id={`foreman`}
            value={selectedForemanUserId}
            size="small"
            handleClear={() => setSelectedForemanUserId('')}
            onChange={e => {
              setSelectedForemanUserId(e.target.value);
            }}
          >
            {!isLoadingAvailableForemanUsers && availableForemanUsers?.length === 0 && (
              <MenuItem disabled>No available users</MenuItem>
            )}
            {!isLoadingAvailableForemanUsers &&
              availableForemanUsers?.map((record, index) => {
                return (
                  <MenuItem
                    key={`${index}`}
                    value={record.value}
                    onClick={() => {
                      setSelectedForemanUserName(record.description);
                    }}
                  >
                    {record.description}
                  </MenuItem>
                );
              })}
          </SMBSelect>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            type="number"
            fullWidth
            size="small"
            autoComplete="nope"
            label="Priority Order"
            name="priorityOrder"
            value={foremanUserPriorityOrder}
            onChange={e => {
              setForemanUserPriorityOrder(e.target.value);
            }}
          />
        </Grid>
      </Grid>
      <ButtonGroup>
        <Box mb={1} mt={1}>
          <Button
            className={classes.button}
            startIcon={<Add />}
            sx={{ minWidth: '14rem' }}
            color="primary"
            variant="contained"
            disabled={!selectedForemanUserId || !foremanUserPriorityOrder}
            onClick={async () => {
              try {
                await addForemanToNeighborhood(
                  neighborhoodId,
                  selectedForemanUserId,
                  foremanUserPriorityOrder
                );
                refetchAvailableForemanUsers();
                refetchNeighborhoodForemanUsers();
                setSelectedForemanUserId('');
                setForemanUserPriorityOrder('');
                enqueueSnackbar('Foreman added!', {
                  variant: 'success'
                });
              } catch (error) {
                enqueueSnackbar(error?.Detail ?? 'Error adding Foreman.', {
                  variant: 'error'
                });
              }
            }}
          >
            Add
          </Button>
          <Button
            className={classes.button}
            startIcon={<Close />}
            sx={{ minWidth: '14rem' }}
            color="inherit"
            variant="contained"
            disabled={!selectedForemanUserId}
            onClick={() => {
              setSelectedForemanUserId('');
              setForemanUserPriorityOrder('');
            }}
          >
            Cancel
          </Button>
        </Box>
      </ButtonGroup>
      {foremanUserNeighborhoodNames && !isLoadingForemanUserNeighborhoodNames && (
        <Typography className={classes.roleDetails}>
          {formatDetails(foremanUserNeighborhoodNames)}
        </Typography>
      )}
      <Divider />
      <Grid item xs={12}>
        <Table
          columns={columns}
          data={neighborhoodsForemanUsers ?? []}
          hidePagination
          isLoading={isLoadingNeighborhoodForemanUsers}
        />
      </Grid>
    </Card>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    padding: theme.spacing(2)
  },
  button: {
    whiteSpace: 'nowrap'
  },
  buttonGroup: {
    marginTop: theme.spacing(1)
  },
  roleDetails: {
    paddingBottom: theme.spacing(1)
  },
  selectWrapper: {
    width: '50%',
    marginBottom: '12px',
    '@media (max-width: 768px)': {
      width: '100%'
    }
  },
  textfieldWrapper: {
    width: '50%',
    '@media (max-width: 768px)': {
      width: '100%'
    }
  },
  alertTitle: {
    fontWeight: 'bold'
  }
}));
